import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SUPPORTED_LANGUAGES, loadAndChangeLanguage, normalizeLanguageCode } from '../../i18n';
import './language-selector.css';

const LANGUAGE_FLAGS = {
  en: '🇺🇸',
  es: '🇪🇸',
  fr: '🇫🇷',
  de: '🇩🇪',
  pt: '🇧🇷',
  ru: '🇷🇺',
  zh: '🇨🇳',
  ja: '🇯🇵',
  ko: '🇰🇷',
  hi: '🇮🇳',
  ar: '🇸🇦',
  he: '🇮🇱',
};

function getLanguageMatch(code) {
  const normalizedCode = normalizeLanguageCode(code);
  return SUPPORTED_LANGUAGES.find((lang) => normalizedCode === lang.code);
}

export default function LanguageSelector({ className = '' }) {
  const { i18n, t } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  const current = useMemo(
    () => getLanguageMatch(i18n.language) || SUPPORTED_LANGUAGES[0],
    [i18n.language]
  );

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (ref.current && !ref.current.contains(event.target)) {
        setOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const translatedLabel = t('common.language', 'Language');
  const label = translatedLabel === 'Language' ? (current.selectorLabel || translatedLabel) : translatedLabel;

  return (
    <div className={`sp-lang-selector ${className}`.trim()} ref={ref} data-no-translate="true">
      <button
        type="button"
        className="sp-lang-button"
        onClick={() => setOpen((value) => !value)}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={label}
      >
        <span className="sp-lang-flag" aria-hidden="true">
          {LANGUAGE_FLAGS[current.code] || '🌐'}
        </span>
        <span className="sp-lang-copy">
          <span className="sp-lang-label">{label}</span>
          <span className="sp-lang-value">{current.nativeName}</span>
        </span>
        <span className="sp-lang-chevron" aria-hidden="true">
          {open ? '▲' : '▼'}
        </span>
      </button>

      {open ? (
        <div className="sp-lang-dropdown" role="listbox" aria-label={label}>
          {SUPPORTED_LANGUAGES.map((lang) => (
            <button
              key={lang.code}
              type="button"
              className={`sp-lang-option ${lang.code === current.code ? 'active' : ''}`}
              onClick={() => {
                loadAndChangeLanguage(lang.code);
                setOpen(false);
              }}
            >
              <span className="sp-lang-flag" aria-hidden="true">
                {LANGUAGE_FLAGS[lang.code] || '🌐'}
              </span>
              <span className="sp-lang-option-copy">
                <span className="sp-lang-option-native">{lang.nativeName}</span>
                <span className="sp-lang-option-name">{lang.name}</span>
              </span>
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}
